import Form from "common/Form/Form.tsx";
import Input from "common/Inputs/Input.tsx";
import React, { useEffect, useMemo, useState } from "react";
import { FormWrapper } from "features/affiliation/common/styles.ts";
import { PAYOUT_SERVICES } from "features/affiliation/common/constants.ts";
import Radio from "common/Radio/Radio.tsx";
import {
  PayPalFullLogo,
  RowCryptoCoins,
  WarningIcon,
} from "common/Icons/Icons.tsx";
import styled, { css, useTheme } from "styled-components";
import { TriangleAlert } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import {
  CreatePayoutMethodRequest,
  CryptoAsset,
  CryptoNetwork,
  LanguageNameSpaces,
  PaymentMethodType,
  PayoutMethod,
  PayoutMethodSource,
} from "@shared/types";
import Select from "common/Select/Select.tsx";
import Title from "common/Typography/Title.tsx";
import {
  CRYPTO_ASSET_TO_DISPLAY,
  CRYPTO_ASSET_TO_NETWORKS,
  INVOICES_CRYPTO_ASSETS,
  CRYPTO_NETWORK_TO_DISPLAY,
  MAX_XRP_TAG_VALUE,
  AFFILIATION_CRYPTO_ASSETS,
} from "features/payouts/PayoutMethods/constants.ts";
import {
  isValidXrpTag,
  validateAddress,
} from "features/payouts/PayoutMethods/utils.ts";
import CustomInputNumber from "common/Inputs/CustomInputNumber.tsx";
import Text from "common/Typography/Text.tsx";
import { hexToRGB } from "@shared/utils/misc.ts";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface CreatePayoutMethodFormProps {
  onSubmit: (values: CreatePayoutMethodRequest) => void;
  isEdit?: boolean;
  payoutMethod?: PayoutMethod;
  asset?: CryptoAsset;
  setAsset: (asset: CryptoAsset) => void;
  network?: CryptoNetwork;
  setNetwork: (network: CryptoNetwork) => void;
  source: PayoutMethodSource;
}

const CreateOrEditPayoutMethodForm = ({
  onSubmit,
  isEdit,
  payoutMethod,
  asset,
  setAsset,
  network,
  setNetwork,
  source,
}: CreatePayoutMethodFormProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PAYOUTS);
  const { isNewLook } = useTheme();
  const initialPayoutService =
    payoutMethod?.payout_method_type || PaymentMethodType.PAYPAL;

  const [payoutService, setPayoutService] = useState(initialPayoutService);

  const cryptoAssets =
    source === PayoutMethodSource.AFFILIATION
      ? AFFILIATION_CRYPTO_ASSETS
      : INVOICES_CRYPTO_ASSETS;

  useEffect(() => {
    if (!asset) {
      const initialAsset = payoutMethod?.asset ?? cryptoAssets[0];
      setAsset(initialAsset);
    }
  }, [payoutMethod, asset, cryptoAssets]);

  useEffect(() => {
    if (!network && asset) {
      const initialNetwork =
        payoutMethod?.network ?? CRYPTO_ASSET_TO_NETWORKS[asset][0];
      setNetwork(initialNetwork);
    }
  }, [payoutMethod, network, asset]);

  const assetOptions = useMemo(
    () =>
      cryptoAssets.map((asset) => ({
        label: CRYPTO_ASSET_TO_DISPLAY[asset],
        value: asset,
      })),
    [cryptoAssets]
  );

  const networkOptions = useMemo(() => {
    if (!asset) return;
    return CRYPTO_ASSET_TO_NETWORKS[asset].map((network) => ({
      label: CRYPTO_NETWORK_TO_DISPLAY[network],
      value: network,
    }));
  }, [asset]);

  const onAssetChange = (asset: CryptoAsset) => {
    setNetwork(CRYPTO_ASSET_TO_NETWORKS[asset][0]);
    setAsset(asset);
  };

  const paypalFormItems = (
    <>
      <Form.Item
        label={t("payouts.account_email")}
        name="email"
        initialValue={payoutMethod?.email}
        style={{ marginBottom: 16 }}
        rules={[
          {
            required: !isEdit,
            message: t("payouts.email_required"),
          },
          {
            type: isEdit ? "string" : "email",
            message: t("payouts.email_invalid"),
          },
        ]}
      >
        <Input placeholder={t("payouts.email_placeholder")} disabled={isEdit} />
      </Form.Item>
      {!isEdit && (
        <Form.Item
          name="confirmEmail"
          initialValue={payoutMethod?.email}
          dependencies={["email"]}
          hasFeedback
          rules={[
            {
              required: !isEdit,
              message: t("payouts.confirm_email_required"),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (
                  !value ||
                  getFieldValue("email").toLowerCase() === value.toLowerCase()
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(t("payouts.emails_no_match"));
              },
            }),
          ]}
        >
          <Input placeholder={t("payouts.confirm_email")} disabled={isEdit} />
        </Form.Item>
      )}
    </>
  );

  const cryptoFormItems = (
    <>
      <Item>
        <ItemTitle>{t("payouts.asset")}</ItemTitle>
        <Select
          options={assetOptions}
          value={asset}
          onChange={onAssetChange}
          style={{ width: "100%" }}
          disabled={isEdit}
          zIndex={2000}
          mobileTitle={t("payouts.asset")}
        />
      </Item>
      <Item>
        <ItemTitle>{t("payouts.network")}</ItemTitle>
        <Select
          options={networkOptions}
          value={network}
          onChange={setNetwork}
          style={{ width: "100%" }}
          disabled={isEdit}
          zIndex={2000}
          mobileTitle={t("payouts.network")}
        />
      </Item>
      <Form.Item
        label={t("payouts.address")}
        name="address"
        style={{ width: "100%" }}
        initialValue={payoutMethod?.address}
        rules={[
          { required: true, message: t("payouts.address_required") },
          () => ({
            validator(_, value) {
              return validateAddress(value, asset, network)
                ? Promise.resolve()
                : Promise.reject(
                    new Error(
                      t("payouts.address_validation_error", {
                        asset: CRYPTO_ASSET_TO_DISPLAY[asset || ""],
                      })
                    )
                  );
            },
          }),
        ]}
      >
        <Input
          disabled={isEdit}
          placeholder={t("payouts.address_placeholder")}
        />
      </Form.Item>
      {asset === CryptoAsset.XRP && (
        <Form.Item
          label={t("payouts.tag")}
          name="tag"
          style={{ width: "100%" }}
          initialValue={payoutMethod?.tag}
          rules={[
            () => ({
              validator(_, value) {
                return isValidXrpTag(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        t("payouts.tag_validation_error", {
                          max: MAX_XRP_TAG_VALUE,
                        })
                      )
                    );
              },
            }),
          ]}
        >
          <CustomInputNumber
            disabled={isEdit}
            placeholder={t("payouts.tag_placeholder")}
            stringMode={true}
          />
        </Form.Item>
      )}
      <WarningMessage>
        {isNewLook ? (
          <TriangleAlert size={16} strokeWidth={1.75} />
        ) : (
          <StyledWarningIcon />
        )}
        {t("payouts.address_warning")}
      </WarningMessage>
    </>
  );

  const form = (
    <Form
      id="createPayoutMethodForm"
      name="createPayout"
      layout="vertical"
      onFinish={onSubmit}
      requiredMark={false}
      style={{ width: "100%" }}
    >
      <Form.Item
        label={t("payouts.name")}
        name="name"
        initialValue={payoutMethod?.name}
        rules={[
          { required: true, message: t("payouts.name_required") },
          {
            max: MAX_NAME_LENGTH,
            message: t("payouts.name_max_length", {
              length: MAX_NAME_LENGTH,
            }),
          },
        ]}
      >
        <Input placeholder={t("payouts.name_placeholder")} />
      </Form.Item>
      <Form.Item
        label={t("payouts.payout_service")}
        name="type"
        initialValue={initialPayoutService}
      >
        <Radio.Group
          value={payoutService}
          onChange={(e) => setPayoutService(e.target.value)}
          disabled={isEdit}
          style={{ display: "flex", columnGap: isNewLook ? 12 : 24 }}
        >
          {PAYOUT_SERVICES.map((payoutService, index) => (
            <StyledRadio key={index} value={payoutService}>
              {PAYOUT_METHOD_TO_SVG[payoutService]}
            </StyledRadio>
          ))}
        </Radio.Group>
      </Form.Item>
      {payoutService === PaymentMethodType.PAYPAL
        ? paypalFormItems
        : cryptoFormItems}
    </Form>
  );

  // New look: every field label (antd's and the selects') in the label role.
  return (
    <FormWrapper>
      {isNewLook ? <NewLookFields>{form}</NewLookFields> : form}
    </FormWrapper>
  );
};

export default CreateOrEditPayoutMethodForm;

const MAX_NAME_LENGTH = 32;

const PAYOUT_METHOD_TO_SVG: {
  [paymentMethod in PaymentMethodType]: React.JSX.Element;
} = {
  [PaymentMethodType.PAYPAL]: (
    <PayPalFullLogo style={{ fontSize: 115, height: 42 }} />
  ),
  [PaymentMethodType.CRYPTO]: (
    <RowCryptoCoins
      style={{ fontSize: 80, height: 42, top: 2, position: "relative" }}
    />
  ),
};

const StyledRadio = styled(Radio)`
  display: flex;
  align-items: center;
  .ant-radio-label {
    display: flex;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      &.ant-radio-wrapper {
        flex: 1 1 0;
        min-width: 0;
        height: 56px;
        margin: 0;
        padding: 0 14px;
        border: 1px solid ${getNewLookTokens(p.theme).hairline};
        border-radius: ${NEW_LOOK_RADIUS.control}px;
        transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
      }
      &.ant-radio-wrapper:not(.ant-radio-wrapper-disabled):hover {
        border-color: ${p.theme.colors.primaryBlue};
      }
      &.ant-radio-wrapper-checked {
        border-color: ${getNewLookTokens(p.theme).accent};
        background: ${hexToRGB(getNewLookTokens(p.theme).accent, 0.06)};
      }
      .ant-radio-label {
        flex: 1;
        justify-content: center;
      }
      .ant-radio-label .anticon {
        height: 30px !important;
      }
    `}
`;
const Item = styled.div`
  width: 100%;
  margin-bottom: 24px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-bottom: 16px;
    `}
`;
const ItemTitle = styled(Title).attrs({ level: 4 })`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        margin-bottom: 6px;
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary};
      }
    `}
`;
const WarningMessage = styled(Text)`
  background: ${(p) => p.theme.colors.lightBackground};
  padding: 12px;
  border-radius: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 12px 16px;
      border-radius: ${NEW_LOOK_RADIUS.control}px;
      background: ${hexToRGB(getNewLookTokens(p.theme).stop, 0.08)};
      color: ${getNewLookTokens(p.theme).text};
      ${newLookType.meta};
      font-size: 12.5px;
      line-height: 18px;
      > svg {
        flex: none;
        margin-top: 1px;
        color: ${getNewLookTokens(p.theme).stop};
      }
    `}
`;
const StyledWarningIcon = styled(WarningIcon)`
  margin-right: 6px;
  font-size: 20px;
  top: 3px;
  position: relative;
`;
const NewLookFields = styled.div`
  .ant-form-item-label > label {
    ${newLookType.label};
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
  .ant-form-item {
    margin-bottom: 16px;
  }
`;
